import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Eye, EyeOff, Trash2, Paperclip, AlertTriangle, CheckCircle, XCircle, ChevronRight } from 'lucide-react';

// Documents module theme tokens
const DOCS_THEME = {
  card: {
    bg: '#FAFAF9',
    border: '#E5E4E2',
    borderLeft: '#A8A5A1',
    shadow: '0 1px 3px 0 rgb(0 0 0 / 0.05)',
    hoverShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08)',
  },
  text: {
    primary: '#3D3B39',
    secondary: '#5A5856',
    muted: '#7A7876',
  },
  status: {
    active: {
      bg: '#E8F5E9',
      text: '#2D5A3D',
      border: '#C8E6C9',
    },
    expired: {
      bg: '#FDF5F5',       // Very soft pink
      text: '#8B5252',     // Muted red
      border: '#F0C8C8',   // Soft red border
      leftBorder: '#D4A5A5', // Softer left accent
    },
    expiringSoon: {
      bg: '#FEF9EE',       // Soft amber
      text: '#8A6B3D',     // Muted amber text
      border: '#F5E6C8',   // Soft amber border
      leftBorder: '#E4C890', // Amber left accent
    },
    replaced: {
      bg: '#F5F5F4',
      text: '#78716C',
      border: '#E7E5E4',
    }
  },
  accent: '#A8A5A1',
};

const DocumentCard = ({ document, onDelete, onView }) => {
  const navigate = useNavigate();
  const [showSensitive, setShowSensitive] = useState(false);
  
  const maskValue = (value) => {
    if (!value) return null;
    if (showSensitive) return value;
    if (value.length <= 4) return '••••';
    return '••••' + value.slice(-4);
  };
  
  const getStatusConfig = (status) => {
    const configs = {
      active: DOCS_THEME.status.active,
      expired: DOCS_THEME.status.expired,
      replaced: DOCS_THEME.status.replaced,
    };
    return configs[status] || configs.active;
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    const icons = {
      active: CheckCircle,
      expired: XCircle,
      replaced: null,
    };
    const labels = {
      active: 'Active',
      expired: 'Expired',
      replaced: 'Replaced',
    };
    const Icon = icons[status];
    
    return (
      <Badge 
        className="font-medium"
        style={{ 
          backgroundColor: config.bg, 
          color: config.text,
          border: `1px solid ${config.border}`,
        }}
      >
        {Icon && <Icon className="h-3 w-3 mr-1" />}
        {labels[status] || 'Active'}
      </Badge>
    );
  };
  
  const getDaysUntilExpiry = () => {
    if (!document.expiration_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(document.expiration_date + 'T00:00:00');
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff;
  };
  
  const daysUntilExpiry = getDaysUntilExpiry();
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;

  // Determine card border color
  const getLeftBorderColor = () => {
    if (isExpired) return DOCS_THEME.status.expired.leftBorder;
    if (isExpiringSoon) return DOCS_THEME.status.expiringSoon.leftBorder;
    return DOCS_THEME.card.borderLeft;
  };

  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    navigate(`/documents/${document.id}`);
  };

  return (
    <Card 
      className="cursor-pointer transition-all duration-200 hover:translate-y-[-1px] overflow-hidden"
      style={{
        backgroundColor: DOCS_THEME.card.bg,
        border: `1px solid ${DOCS_THEME.card.border}`,
        borderLeft: `4px solid ${getLeftBorderColor()}`,
        boxShadow: DOCS_THEME.card.shadow,
        borderRadius: '0.75rem',
      }}
      onClick={handleCardClick}
      data-testid={`document-card-${document.id}`}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Header with title and badges */}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h4 
                className="font-semibold truncate"
                style={{ color: DOCS_THEME.text.primary }}
              >
                {document.title}
              </h4>
              {document.status && getStatusBadge(document.status)}
              {document.document_type && (
                <Badge 
                  variant="outline" 
                  className="text-xs"
                  style={{ 
                    borderColor: DOCS_THEME.card.border,
                    color: DOCS_THEME.text.secondary,
                  }}
                >
                  {document.document_type}
                </Badge>
              )}
              {document.attachments && document.attachments.length > 0 && (
                <Badge 
                  className="text-xs"
                  style={{ 
                    backgroundColor: '#F0EFED',
                    color: DOCS_THEME.text.secondary,
                  }}
                >
                  <Paperclip className="h-3 w-3 mr-1" />
                  {document.attachments.length}
                </Badge>
              )}
            </div>
            
            {/* Expiry Warning - Softer pastel style */}
            {isExpiringSoon && (
              <div 
                className="mb-3 px-3 py-2 rounded-lg text-sm flex items-center gap-2"
                style={{
                  backgroundColor: DOCS_THEME.status.expiringSoon.bg,
                  border: `1px solid ${DOCS_THEME.status.expiringSoon.border}`,
                  color: DOCS_THEME.status.expiringSoon.text,
                }}
              >
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>Expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</span>
              </div>
            )}
            {isExpired && (
              <div 
                className="mb-3 px-3 py-2 rounded-lg text-sm flex items-center gap-2"
                style={{
                  backgroundColor: DOCS_THEME.status.expired.bg,
                  border: `1px solid ${DOCS_THEME.status.expired.border}`,
                  color: DOCS_THEME.status.expired.text,
                }}
              >
                <XCircle className="h-4 w-4 flex-shrink-0" />
                <span>Expired {Math.abs(daysUntilExpiry)} day{Math.abs(daysUntilExpiry) !== 1 ? 's' : ''} ago</span>
              </div>
            )}
            
            {/* Document Details */}
            <div className="space-y-1.5 text-sm" style={{ color: DOCS_THEME.text.secondary }}>
              {document.issuer && (
                <p>
                  <span className="font-medium" style={{ color: DOCS_THEME.text.primary }}>Issuer:</span>{' '}
                  {document.issuer}
                </p>
              )}
              
              {/* Key Number - Masked */}
              {document.key_number && (
                <div className="flex items-center gap-2">
                  <span className="font-medium" style={{ color: DOCS_THEME.text.primary }}>ID/Number:</span>
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                    {maskValue(document.key_number)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSensitive(!showSensitive);
                    }}
                    className="h-6 w-6 p-0 hover:bg-gray-100"
                  >
                    {showSensitive ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              )}
              
              {/* Insurance specific */}
              {document.insurance_type && (
                <p>
                  <span className="font-medium" style={{ color: DOCS_THEME.text.primary }}>Type:</span>{' '}
                  {document.insurance_type}
                </p>
              )}
              {document.plan_name && (
                <p>
                  <span className="font-medium" style={{ color: DOCS_THEME.text.primary }}>Plan:</span>{' '}
                  {document.plan_name}
                </p>
              )}
              {document.group_number && (
                <div className="flex items-center gap-2">
                  <span className="font-medium" style={{ color: DOCS_THEME.text.primary }}>Group:</span>
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                    {maskValue(document.group_number)}
                  </span>
                </div>
              )}
              
              {/* License specific */}
              {document.license_number && (
                <div className="flex items-center gap-2">
                  <span className="font-medium" style={{ color: DOCS_THEME.text.primary }}>License #:</span>
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                    {maskValue(document.license_number)}
                  </span>
                </div>
              )}
              
              {/* Dates */}
              <div className="flex items-center gap-4 flex-wrap mt-3 pt-2 border-t" style={{ borderColor: '#F0EFED' }}>
                {document.issue_date && (
                  <span className="flex items-center gap-1.5" style={{ color: DOCS_THEME.text.muted }}>
                    <Calendar className="h-3.5 w-3.5" />
                    <span className="text-xs">Issued: {new Date(document.issue_date + 'T00:00:00').toLocaleDateString()}</span>
                  </span>
                )}
                {document.expiration_date && (
                  <span 
                    className="flex items-center gap-1.5 text-xs"
                    style={{ 
                      color: isExpired 
                        ? DOCS_THEME.status.expired.text 
                        : isExpiringSoon 
                          ? DOCS_THEME.status.expiringSoon.text 
                          : DOCS_THEME.text.muted 
                    }}
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    Expires: {new Date(document.expiration_date + 'T00:00:00').toLocaleDateString()}
                  </span>
                )}
              </div>
              
              {/* Tags */}
              {document.tags && document.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {document.tags.map((tag, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline" 
                      className="text-xs"
                      style={{ 
                        borderColor: '#E5E4E2',
                        color: DOCS_THEME.text.muted,
                        backgroundColor: '#F8F7F6',
                      }}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              
              {document.notes && (
                <p 
                  className="italic mt-2 text-xs line-clamp-2"
                  style={{ color: DOCS_THEME.text.muted }}
                >
                  {document.notes}
                </p>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(document.id);
                }}
                className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <ChevronRight className="h-5 w-5" style={{ color: DOCS_THEME.text.muted }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentCard;
